/**
 * 딜등록 표시용 다듬기 — 딜등록 화면과 대시보드가 **같이 쓴다** (v2.142.0 · 2026-09-08)
 *
 * 판정이 아니다 — 서버가 준 값(isClosed · dday)을 눈에 잘 들어오게 줄이는 것뿐이다.
 * 「만료 임박」의 날수(EXPIRING_DAYS)도 여기 한 곳에만 있다(대시보드 요청의 days 와 화면 배지가 같은 숫자).
 */

import type { DealRow, DealStatus } from './deal-registration-api';

export const STATUS_BADGE: Record<DealStatus, string> = {
  /** 승인대기 — 아직 인포트렌드 접수번호가 없는 건 (v2.189.0). 눈에 띄게 노랑 */
  Pending: 'bg-yellow-100 text-yellow-800',
  'On-going': 'bg-sky-100 text-sky-800',
  Extend: 'bg-violet-100 text-violet-800',
  Processing: 'bg-amber-100 text-amber-800',
  Won: 'bg-emerald-100 text-emerald-800',
  Lost: 'bg-rose-100 text-rose-800',
  Invalid: 'bg-slate-200 text-slate-700',
  /** 거절 — 인포트렌드가 등록을 거절한 건 (v2.189.0). 실주와 같은 계열이되 더 진하게 */
  Rejected: 'bg-rose-200 text-rose-900',
};

/** 「만료 임박」 = 연장 마감이 이 날수 안(지난 것 포함) */
export const EXPIRING_DAYS = 30;

const EMAIL_RE = /[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/g;
const PHONE_RE = /0\d{1,2}[-.\s]?\d{3,4}[-.\s]?\d{4}/g;

/** 제안사 칸 — 첫 줄에서 이메일·전화만 떼고 보여 준다(전체는 상세·마우스 올림). 「에스엠시스템즈/⏎SMSYSTEMS」 는 앞 줄만 */
export function tier2Short(t: string): string {
  const first = t.split('\n').map((l) => l.trim()).filter(Boolean)[0] ?? '';
  return first.replace(EMAIL_RE, '').replace(PHONE_RE, '').replace(/[<>()]/g, ' ').replace(/\s+/g, ' ').replace(/[\s/]+$/, '').trim();
}
/** 제안사 칸의 첫 메일 주소 — 정보 요청 창의 받는사람 미리 채우기 */
export function tier2Email(t: string): string {
  return (t.match(EMAIL_RE) ?? [])[0] ?? '';
}

/**
 * 제안사 이름 한 줄 (v2.143.0) — 거래처에 연결됐으면 그 이름, 담당자만 연결됐으면 담당자의 회사 글자, 둘 다 없으면 제안사 칸 글자.
 * 딜등록 표 · 대시보드 · 정보 요청 창이 같이 쓴다.
 */
export function dealCompanyLabel(row: Pick<DealRow, 'tier2CompanyName' | 'tier2ContactCompany' | 'tier2'>): string {
  return row.tier2CompanyName || row.tier2ContactCompany || tier2Short(row.tier2);
}

/**
 * 엔드유저 칸에서 **이름** 하나를 — ⭐**자리(번호)가 아니라 글자로** 고른다.
 *
 * ⛔⛔**종전에는 「2번 줄」을 집었는데 이것이 v2.193.0 에 깨질 자리였다.** 엔드유저를 세 칸으로
 *   나누면서 표준 글자가 **「1. 한글 / 2. 영문 / 3. 홈페이지」** 로 바뀌기 때문에, 자리로 집으면
 *   **표·대시보드·엑셀에 영문이 뜬다.** 그래서 서버의 쌍둥이(`lib/deal-registration/expiry-mail.ts`
 *   `endUserLabel`)와 **같은 규칙** — 홈페이지 줄을 뺀 뒤 **한글이 든 첫 줄**, 한글이 아예 없으면 첫 줄 —
 *   으로 맞췄다. 이러면 옛 자료(1번 영문·2번 한글)와 새 표준 둘 다 맞는다.
 * ⚠번호 뒤 구분자가 제각각이다 — '1)' · '1.' · 전각 '1．' · '1）' 전부 받는다.
 * ⭐줄이 있는 딜은 `endUserKo` 칸을 그대로 쓰는 쪽이 더 정확하다 — `dealEndUser()` 를 쓸 것.
 */
export function endUserShort(t: string): string {
  const lines = t
    .replace(/\s*(?:^|\s)([123])[).．）]\s*/g, '\n$1) ')
    .split('\n')
    .map((l) => l.replace(/^[123][).．）]\s*/, '').replace(/\s+/g, ' ').trim())
    .filter(Boolean)
    .filter((l) => !/^(?:https?:\/\/|www\.)/i.test(l));
  return lines.find((l) => /[가-힣]/.test(l)) ?? lines[0] ?? '';
}

/**
 * 딜 한 줄의 **엔드유저 이름** — 한글 칸이 있으면 그것, 없으면 옛 글자에서 뽑는다 (v2.193.0).
 * 서버 `lib/deal-registration/end-user.ts` 의 `endUserName()` 과 같은 규칙이다.
 */
export function dealEndUser(r: { endUserKo?: string; endUser?: string }): string {
  const ko = (r.endUserKo ?? '').trim();
  return ko || endUserShort(r.endUser ?? '');
}

/** 프로젝트 칸 — 「Project Name : …」 줄이 있으면 그것, 없으면 첫 줄 */
export function projectTitle(info: string): string {
  const lines = info.split('\n').map((l) => l.trim()).filter(Boolean);
  // ⚠구분자가 쌍점만이 아니다 — 운영 자료에 `1．Project Name ; …` (전각 마침표 + 쌍반점)가 흔하다.
  //   쌍반점을 못 읽던 동안 그 딜들은 제목 자리에 「Spec in Model : …」 이 나오고 있었다 (v2.190.0 에서 고침).
  //   서버(lib/deal-registration/notice-mail.ts PROJECT_NAME_LINE)와 **같은 모양**을 본다 — 표와 메일이 갈리면 안 된다.
  const named = lines.find((l) => /project\s*name\s*[:;：；]/i.test(l) || /프로젝트명\s*[:;：；]/.test(l));
  if (named) return named.replace(/^.*?(?:project\s*name|프로젝트명)\s*[:;：；]\s*/i, '').trim();
  return lines[0] ?? '';
}

/**
 * 딜의 프로젝트명 — **적어 둔 칸이 있으면 그것**, 없으면 프로젝트 정보에서 뽑아 쓴다 (v2.189.0).
 *
 * 프로젝트명 칸(topic)은 v2.189.0 에 생겼다. 그 전 자료 206건은 칸이 비어 있고 프로젝트명이
 * 「프로젝트 정보」 글 안에 「1. Project Name : …」 으로 섞여 있다 — 옛 자료도 그대로 보이게
 * **두 길을 다 본다.** ⛔옛 자료를 일괄로 옮겨 적지 않는다(글에서 뽑는 것이라 틀릴 수 있다).
 */
export function dealTopic(row: Pick<DealRow, 'topic' | 'projectInfo'>): string {
  return row.topic.trim() || projectTitle(row.projectInfo);
}

/** 프로젝트 칸 둘째 줄 — 「Spec in Model : …」 이 있으면 그것, 없으면 P/N */
export function specLine(info: string, pn: string): string {
  const spec = info.split('\n').map((l) => l.trim()).find((l) => /^spec/i.test(l));
  if (spec) return spec.replace(/^spec[^:：]*[:：]\s*/i, '').trim();
  return pn.split('\n')[0]?.trim() ?? '';
}

export function ddayText(row: Pick<DealRow, 'isClosed' | 'dday'>): { text: string; cls: string } | null {
  if (row.isClosed || row.dday === null) return null;
  if (row.dday < 0) return { text: `${-row.dday}일 지남`, cls: 'bg-rose-100 text-rose-700' };
  if (row.dday === 0) return { text: '오늘', cls: 'bg-rose-100 text-rose-700' };
  if (row.dday <= EXPIRING_DAYS) return { text: `D-${row.dday}`, cls: 'bg-amber-100 text-amber-800' };
  return { text: `D-${row.dday}`, cls: 'bg-slate-100 text-slate-600' };
}

export function isExpiring(row: Pick<DealRow, 'isClosed' | 'dday'>): boolean {
  return !row.isClosed && row.dday !== null && row.dday <= EXPIRING_DAYS;
}

/**
 * **24일 검토 표 · 25일 엑셀에 나갈 딜**인가 (v2.215.0) — 서버 `monthly-notice.ts` `loadReviewDeals` 와 같은 판정:
 * 진행 중 탭(종료 탭으로 안 넘긴 것) · 승인대기 아님 · 연장 마감 30일 안(지난 것 포함).
 * ⭐`isExpiring` 과 달리 **실주·무효로 바꾼 딜도 넘기기 전이면 들어간다** — 바뀐 상태를 표에서 검토하려는 것이다.
 * 목록의 [⏰ 만료 임박] 단추가 이 판정을 쓴다(표에 무엇이 나갈지 미리 보이게). 대시보드는 그대로 `isExpiring`.
 */
export function isMonthlyReviewRow(row: Pick<DealRow, 'archived' | 'status' | 'dday'>): boolean {
  return !row.archived && row.status !== 'Pending' && row.dday !== null && row.dday <= EXPIRING_DAYS;
}

/** '2026-09-08' → '09-08' (칸이 좁은 자리용) */
export function mmdd(ymd: string | null): string {
  return ymd ? ymd.slice(5) : '';
}
